import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FormTemplate, FormTemplateSection } from 'data/template/template.types';
import * as types from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.types';
import Form, {
  FieldUpdateHandlerParam,
} from 'shared/form';
import { FormLayout, FormLayoutContent } from 'components/FormLayout';
import FormPanel from 'components/FormPanel';
import FormButtons from 'components/FormButtons';
import { TargetTemplateFormSection } from 'routes/TargetDeliveryTemplateAccessFunction/TargetDeliveryTemplateAccessFunction.types';
import * as _ from 'lodash';
import TargetDeliveryTemplateInterfaceForm from '../TargetDeliveryTemplateInterfaceForm';
import './dialed-digit-extraction-server-form.scss';

export type FieldHandler = (
  param: FieldUpdateHandlerParam,
  formSection: TargetTemplateFormSection,
  tabId?: string) => void

type Props = {
  template: FormTemplate,
  data: Partial<types.TargetDeliveryTemplateAccessFunctionFormData>
  i18nKeyPrefix?: string
  isEditing: boolean,
  title?: string,
  onFieldUpdate?: FieldHandler,
  onClickSave?: () => void
  onClickEdit?: () => void
  onClickCancelClose: () => void
  onClickCopy?: () => void,
  onClickDelete?: () => void,
  onInterfaceAdd?: (formSection: TargetTemplateFormSection) => void
  onInterfaceRemove?: (id: string | number, formSection: TargetTemplateFormSection) => void
}

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.TargetDeliveryTemplateAccessFunctionFormData
}

export const TARGET_DELIVERY_TEMPLATE_I18N_KEY = 'targetDeliveryTemplateAccessFunction.form';

export const TargetDeliveryTemplateAccessFunctionForm = forwardRef<Ref, Props>(({
  template,
  data,
  i18nKeyPrefix = TARGET_DELIVERY_TEMPLATE_I18N_KEY,
  title,
  isEditing,
  onFieldUpdate = () => null,
  onClickSave = () => null,
  onClickEdit = () => null,
  onClickCancelClose = () => null,
  onInterfaceAdd = () => null,
  onInterfaceRemove = () => null,
}: Props, ref) => {
  const mainRef = useRef<Form>(null);
  const targetInformationRef = useRef<any>(null);
  const EIMSGENERALRef = useRef<any>(null);
  const EIMSTARGETRef = useRef<any>(null);
  const EIMSGPRSTRGTRef = useRef<any>(null);
  const EIMSMSCTRGTRef = useRef<any>(null);
  const EIMSMCNBRef = useRef<any>(null);
  const EIMSWRLNTRGTRef = useRef<any>(null);
  const EIMSMCRef = useRef<any>(null);
  const callContentChannelsRef = useRef<any>(null);
  const callContentTypeRef = useRef<any>(null);
  const monitoringCenterNumberRef = useRef<any>(null);
  const icdRef = useRef<any>(null);
  const monitoringCenterFlagsRef = useRef<any>(null);
  const networkTypeRef = useRef<any>(null);
  const usnbdTargetInformationRef = useRef<any>(null);
  const usnbdCallContentChannelInformationRef = useRef<any>(null);
  const { t } = useTranslation();

  const isValid = (forceValidation = false, forceTouched = true) => {
    const isMainValid = mainRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const targetInformationValid = targetInformationRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const callContentChannelsValid = callContentTypeRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const callContentTypeRefvalid = callContentChannelsRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const monitoringCenterNumberValid = monitoringCenterNumberRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const icdRefvalid = icdRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const monitoringCenterFlagsRefvalid =
      monitoringCenterFlagsRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const networkTypeRefvalid = networkTypeRef.current?.isValid(forceValidation, forceTouched) ?? true;

    const EIMSGENERALValid = EIMSGENERALRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const EIMSTARGETValid = EIMSTARGETRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const EIMSGPRSTRGTValid = EIMSGPRSTRGTRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const EIMSMCNBValid = EIMSMCNBRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const EIMSWRLNTRGTValid = EIMSWRLNTRGTRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const EIMSMCValid = EIMSMCRef.current?.isValid(forceValidation, forceTouched) ?? true;

    const usnbdTargetInformationValid = usnbdTargetInformationRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const usnbdCallContentChannelInformationValid = usnbdCallContentChannelInformationRef.current?.isValid(forceValidation, forceTouched) ?? true;

    return isMainValid && targetInformationValid
      && callContentChannelsValid && callContentTypeRefvalid
      && icdRefvalid && monitoringCenterFlagsRefvalid
      && networkTypeRefvalid
      && monitoringCenterNumberValid
      && EIMSTARGETValid && EIMSGPRSTRGTValid && EIMSMCNBValid && EIMSWRLNTRGTValid && EIMSGENERALValid && EIMSMCValid
      && usnbdTargetInformationValid && usnbdCallContentChannelInformationValid;
  };

  const getValues = () => {
    const formValues: types.TargetDeliveryTemplateAccessFunctionFormData = {
      ...mainRef.current?.getValues(),
      targetInformation: targetInformationRef.current?.getValues(),
      callContentChannels: callContentChannelsRef.current?.getValues(),
      monitoringCenterFlags: monitoringCenterFlagsRef.current?.getValues(),
      networkType: networkTypeRef.current?.getValues(),
      ICD: icdRef.current?.getValues(),
      callContentType: callContentTypeRef.current?.getValues(),
      EIMSTARGET: EIMSTARGETRef.current?.getValues(),
      EIMSMSCTRGT: EIMSMSCTRGTRef.current?.getValues(),
      EIMSGPRSTRGT: EIMSGPRSTRGTRef.current?.getValues(),
      EIMSMCNB: EIMSMCNBRef.current?.getValues(),
      EIMSWRLNTRGT: EIMSWRLNTRGTRef.current?.getValues(),
      EIMSGENERAL: EIMSGENERALRef.current?.getValues(),
      EIMSMC: EIMSMCRef.current?.getValues(),
      usnbdTargetInformation: usnbdTargetInformationRef.current?.getValues(),
      usnbdCallContentChannelInformation: usnbdCallContentChannelInformationRef.current?.getValues(),
    };

    return formValues;
  };

  useImperativeHandle(ref, () => ({
    isValid,
    getValues,
  }));

  const onMainFieldUpdate = (param: FieldUpdateHandlerParam) => onFieldUpdate(param, 'main');

  const renderEricssonNEForms = () => {
    if (template.key !== 'ERICSSON_NE') {
      return;
    }
    return (
      <React.Fragment>
        <TargetDeliveryTemplateInterfaceForm
          ref={EIMSGENERALRef}
          id="eimsgeneral"
          title={t(
            'targetDeliveryTemplateAccessFunction.ericssonNE.general.title'
          )}
          tabTitle={t(
            'targetDeliveryTemplateAccessFunction.ericssonNE.general.title'
          )}
          formSection="EIMSGENERAL"
          isEditing={isEditing}
          template={template}
          data={data}
          i18nKeyPrefix="targetDeliveryTemplateAccessFunction.ericssonNE.EIMSTARGET"
          onFieldUpdate={onFieldUpdate}
          onInterfaceAdd={onInterfaceAdd}
          onInterfaceRemove={onInterfaceRemove}
        />
        {!((template.EIMSTARGET as FormTemplateSection).metadata.hide === 'true') && (
          <TargetDeliveryTemplateInterfaceForm
            ref={EIMSTARGETRef}
            id="eimstargetinfo"
            title={t(
              'targetDeliveryTemplateAccessFunction.ericssonNE.EIMSTARGET.title'
            )}
            tabTitle={t(
              'targetDeliveryTemplateAccessFunction.ericssonNE.EIMSTARGET.title'
            )}
            formSection="EIMSTARGET"
            isEditing={isEditing}
            template={template}
            data={data}
            i18nKeyPrefix="targetDeliveryTemplateAccessFunction.ericssonNE.EIMSTARGET"
            onFieldUpdate={onFieldUpdate}
            onInterfaceAdd={onInterfaceAdd}
            onInterfaceRemove={onInterfaceRemove}
          />
        )}
        {!((template.EIMSMSCTRGT as FormTemplateSection).metadata.hide === 'true') && (
          <TargetDeliveryTemplateInterfaceForm
            ref={EIMSMSCTRGTRef}
            id="eimsmsctrgt"
            title={t(
              'targetDeliveryTemplateAccessFunction.ericssonNE.EIMSMSCTRGT.title'
            )}
            tabTitle={t(
              'targetDeliveryTemplateAccessFunction.ericssonNE.EIMSMSCTRGT.title'
            )}
            formSection="EIMSMSCTRGT"
            isEditing={isEditing}
            template={template}
            data={data}
            i18nKeyPrefix="targetDeliveryTemplateAccessFunction.ericssonNE.EIMSMSCTRGT"
            onFieldUpdate={onFieldUpdate}
            onInterfaceAdd={onInterfaceAdd}
            onInterfaceRemove={onInterfaceRemove}
          />
        )}
        {!((template.EIMSGPRSTRGT as FormTemplateSection).metadata.hide === 'true') && (
          <TargetDeliveryTemplateInterfaceForm
            ref={EIMSGPRSTRGTRef}
            id="eimsgprstrgt"
            title={t(
              'targetDeliveryTemplateAccessFunction.ericssonNE.EIMSGPRSTRGT.title'
            )}
            tabTitle={t(
              'targetDeliveryTemplateAccessFunction.ericssonNE.EIMSGPRSTRGT.title'
            )}
            formSection="EIMSGPRSTRGT"
            isEditing={isEditing}
            template={template}
            data={data}
            i18nKeyPrefix="targetDeliveryTemplateAccessFunction.ericssonNE.EIMSGPRSTRGT"
            onFieldUpdate={onFieldUpdate}
            onInterfaceAdd={onInterfaceAdd}
            onInterfaceRemove={onInterfaceRemove}
          />
        )}
        {!((template.EIMSMC as FormTemplateSection).metadata.hide === 'true') && (
          <TargetDeliveryTemplateInterfaceForm
            ref={EIMSMCRef}
            id="eimsmc"
            title={t(
              'targetDeliveryTemplateAccessFunction.ericssonNE.EIMSMC.title'
            )}
            tabTitle={t(
              'targetDeliveryTemplateAccessFunction.ericssonNE.EIMSMC.title'
            )}
            formSection="EIMSMC"
            isEditing={isEditing}
            template={template}
            data={data}
            i18nKeyPrefix="targetDeliveryTemplateAccessFunction.ericssonNE.EIMSMC"
            onFieldUpdate={onFieldUpdate}
            onInterfaceAdd={onInterfaceAdd}
            onInterfaceRemove={onInterfaceRemove}
          />
        )}
        {!((template.EIMSMCNB as FormTemplateSection).metadata.hide === 'true') && (
          <TargetDeliveryTemplateInterfaceForm
            ref={EIMSMCNBRef}
            id="eimsmcnb"
            title={t(
              'targetDeliveryTemplateAccessFunction.ericssonNE.EIMSMCNB.title'
            )}
            tabTitle="Tab"
            formSection="EIMSMCNB"
            isEditing={isEditing}
            template={template}
            data={data}
            i18nKeyPrefix="targetDeliveryTemplateAccessFunction.ericssonNE.EIMSMCNB"
            onFieldUpdate={onFieldUpdate}
            onInterfaceAdd={onInterfaceAdd}
            onInterfaceRemove={onInterfaceRemove}
            displayMultiTab
          />
        )}

        {!((template.EIMSWRLNTRGT as FormTemplateSection).metadata.hide === 'true') && (
          <TargetDeliveryTemplateInterfaceForm
            ref={EIMSWRLNTRGTRef}
            id="eimswrlntrgt"
            title={t(
              'targetDeliveryTemplateAccessFunction.ericssonNE.EIMSWRLNTRGT.title'
            )}
            tabTitle={t(
              'targetDeliveryTemplateAccessFunction.ericssonNE.EIMSWRLNTRGT.title'
            )}
            formSection="EIMSWRLNTRGT"
            isEditing={isEditing}
            template={template}
            data={data}
            i18nKeyPrefix="targetDeliveryTemplateAccessFunction.ericssonNE.EIMSWRLNTRGT"
            onFieldUpdate={onFieldUpdate}
            onInterfaceAdd={onInterfaceAdd}
            onInterfaceRemove={onInterfaceRemove}
          />
        )}
      </React.Fragment>
    );
  };

  const renderFormPanel = (formTitle: string | null = null) => (
    <FormPanel title={formTitle}>
      <FormButtons
        isEditing={isEditing}
        onClickCancel={onClickCancelClose}
        onClickSave={onClickSave}
        onClickEdit={onClickEdit}
        onClickClose={onClickCancelClose}
      />
    </FormPanel>
  );

  return (
    <div className="dialed-digit-extraction-server-form" data-testid="DialedDigitExtractionServerForm">
      <div data-testid="DialedDigitExtractionServerFormMain">
        <FormLayout>
          <FormLayoutContent>
            {renderFormPanel(title)}
            <Form
              ref={mainRef}
              name={template.formId ?? template.key}
              defaults={data}
              fields={(template.main as FormTemplateSection).fields}
              layout={(template.main as FormTemplateSection).metadata.layout?.rows}
              i18nKeyPrefix={i18nKeyPrefix}
              fieldUpdateHandler={onMainFieldUpdate}
            />
            {renderEricssonNEForms()}
            <TargetDeliveryTemplateInterfaceForm
              ref={targetInformationRef}
              id="targetInformation"
              title={t('targetDeliveryTemplateAccessFunction.form.targetInformation')}
              tabTitle={t('targetDeliveryTemplateAccessFunction.form.targetInformation')}
              formSection="targetInformation"
              isEditing={isEditing}
              template={template}
              data={data}
              i18nKeyPrefix={i18nKeyPrefix}
              onFieldUpdate={onFieldUpdate}
              onInterfaceAdd={onInterfaceAdd}
              onInterfaceRemove={onInterfaceRemove}
            />
            <TargetDeliveryTemplateInterfaceForm
              ref={callContentChannelsRef}
              id="callContentChannelsRef"
              title={t('targetDeliveryTemplateAccessFunction.form.callContentChannels')}
              tabTitle={t('targetDeliveryTemplateAccessFunction.form.callContentChannels')}
              formSection="callContentChannels"
              isEditing={isEditing}
              template={template}
              data={data}
              i18nKeyPrefix={i18nKeyPrefix}
              onFieldUpdate={onFieldUpdate}
              onInterfaceAdd={onInterfaceAdd}
              onInterfaceRemove={onInterfaceRemove}
            />
            <TargetDeliveryTemplateInterfaceForm
              ref={monitoringCenterFlagsRef}
              id="monitoringCenterFlagsRef"
              title={t('targetDeliveryTemplateAccessFunction.form.monitoringCenterFlags')}
              tabTitle={t('targetDeliveryTemplateAccessFunction.form.monitoringCenterFlags')}
              formSection="monitoringCenterFlags"
              isEditing={isEditing}
              template={template}
              data={data}
              i18nKeyPrefix={i18nKeyPrefix}
              onFieldUpdate={onFieldUpdate}
              onInterfaceAdd={onInterfaceAdd}
              onInterfaceRemove={onInterfaceRemove}
            />
            <TargetDeliveryTemplateInterfaceForm
              ref={icdRef}
              id="icdRef"
              title={t('targetDeliveryTemplateAccessFunction.form.ICD')}
              tabTitle={t('targetDeliveryTemplateAccessFunction.form.ICD')}
              formSection="ICD"
              isEditing={isEditing}
              template={template}
              data={data}
              i18nKeyPrefix={i18nKeyPrefix}
              onFieldUpdate={onFieldUpdate}
              onInterfaceAdd={onInterfaceAdd}
              onInterfaceRemove={onInterfaceRemove}
            />
            <TargetDeliveryTemplateInterfaceForm
              ref={networkTypeRef}
              id="networkTypeRef"
              title={t('targetDeliveryTemplateAccessFunction.form.networkType')}
              tabTitle={t('targetDeliveryTemplateAccessFunction.form.networkType')}
              formSection="networkType"
              isEditing={isEditing}
              template={template}
              data={data}
              i18nKeyPrefix={i18nKeyPrefix}
              onFieldUpdate={onFieldUpdate}
              onInterfaceAdd={onInterfaceAdd}
              onInterfaceRemove={onInterfaceRemove}
            />
            <TargetDeliveryTemplateInterfaceForm
              ref={callContentTypeRef}
              id="callContentTypeRef"
              title={t('targetDeliveryTemplateAccessFunction.form.callContentType')}
              tabTitle={t('targetDeliveryTemplateAccessFunction.form.callContentType')}
              formSection="callContentType"
              isEditing={isEditing}
              template={template}
              data={data}
              i18nKeyPrefix={i18nKeyPrefix}
              onFieldUpdate={onFieldUpdate}
              onInterfaceAdd={onInterfaceAdd}
              onInterfaceRemove={onInterfaceRemove}
            />
            <TargetDeliveryTemplateInterfaceForm
              ref={usnbdTargetInformationRef}
              id="usnbdTargetInformationRef"
              title={t('targetDeliveryTemplateAccessFunction.form.usnbdTargetInformation')}
              tabTitle={t('targetDeliveryTemplateAccessFunction.form.usnbdTargetInformation')}
              formSection="usnbdTargetInformation"
              isEditing={isEditing}
              template={template}
              data={data}
              i18nKeyPrefix={i18nKeyPrefix}
              onFieldUpdate={onFieldUpdate}
              onInterfaceAdd={onInterfaceAdd}
              onInterfaceRemove={onInterfaceRemove}
            />
            <TargetDeliveryTemplateInterfaceForm
              ref={usnbdCallContentChannelInformationRef}
              id="usnbdCallContentChannelInformationRef"
              title={t('targetDeliveryTemplateAccessFunction.form.usnbdCallContentChannelInformation')}
              tabTitle={t('targetDeliveryTemplateAccessFunction.form.usnbdCallContentChannelInformation')}
              formSection="usnbdCallContentChannelInformation"
              isEditing={isEditing}
              template={template}
              data={data}
              i18nKeyPrefix={i18nKeyPrefix}
              onFieldUpdate={onFieldUpdate}
              onInterfaceAdd={onInterfaceAdd}
              onInterfaceRemove={onInterfaceRemove}
            />
            {renderFormPanel('')}
          </FormLayoutContent>
        </FormLayout>
      </div>
    </div>
  );
});

export default TargetDeliveryTemplateAccessFunctionForm;
